/**
 * Le statut d'un dossier, en un seul endroit.
 *
 * Il y en avait deux jusqu'au 04/09 — traite, ou pas — portes par le seul
 * booleen `seen`. Nicolas en a demande un troisieme : « Dossier a verifier »,
 * pour qu'une secretaire novice passe la main a une plus experte sans sortir
 * du produit. Trois etats ne tiennent plus dans un booleen, et surtout ils
 * s'affichent a deux endroits (le secretariat et les tuiles « Derniers RDV »)
 * qui avaient deja diverge une fois sur le nommage des fichiers. La regle vit
 * donc ici, et les deux ecrans l'appellent.
 *
 * `seen` reste ecrit et lu : le backend le pose lui-meme quand il genere le
 * PDF d'un dossier (pdf_controller). On ne peut pas le remplacer, on le
 * complete. Quand les deux se contredisent, le statut pose par un humain
 * l'emporte sur celui pose par une machine.
 */

export const STATUT_NOUVEAU = "nouveau";
export const STATUT_TRAITE = "traite";
export const STATUT_A_VERIFIER = "a_verifier";

/** L'etat reel d'un dossier, quel que soit l'age du document en base. */
export function statutDossier(appointment) {
  if (!appointment) return STATUT_NOUVEAU;
  if (appointment.statut === STATUT_A_VERIFIER) return STATUT_A_VERIFIER;
  if (appointment.statut === STATUT_NOUVEAU && !appointment.seen) return STATUT_NOUVEAU;
  if (appointment.statut === STATUT_TRAITE || appointment.seen) return STATUT_TRAITE;
  return STATUT_NOUVEAU;
}

/**
 * Ce qui s'affiche pour chaque statut. Le violet de « A verifier » est le
 * choix de Nicolas : il ne ressemble ni au vert de « traite » ni au rouge
 * d'une erreur, et c'est justement ce qu'on veut dire — ce dossier n'est pas
 * en faute, il attend un deuxieme regard.
 */
export const APPARENCE_STATUT = {
  [STATUT_NOUVEAU]: { libelle: "", couleur: "", fond: "", bordure: "" },
  [STATUT_TRAITE]: {
    libelle: "Traité",
    couleur: "#0b6f3b",
    fond: "#e9fbf2",
    bordure: "#10B981",
  },
  [STATUT_A_VERIFIER]: {
    libelle: "À vérifier",
    couleur: "#5b21b6",
    fond: "#f3ecff",
    bordure: "#7C3AED",
  },
};

/**
 * Les champs a ecrire en base pour poser un statut. `seen` suit, pour que les
 * ecrans et le backend qui ne connaissent que lui restent d'accord avec nous.
 */
export function champsPourStatut(statut) {
  if (statut === STATUT_A_VERIFIER) return { statut: STATUT_A_VERIFIER, seen: false };
  if (statut === STATUT_TRAITE) return { statut: STATUT_TRAITE, seen: true };
  return { statut: STATUT_NOUVEAU, seen: false };
}

/**
 * Le nom lisible d'une personne, a partir de ce qu'on a sous la main.
 *
 * Retour de Nicolas du 04/09 (17h41) : « "Dernier mot laissé par
 * nicolas.desjardins@lorraine.synlab.fr" -> "Dernier message laissé par
 * Prénom et nom". N'utilise pas l'adresse mail. » Il a raison : une adresse
 * professionnelle est illisible, et sur un fil entre collegues on veut le
 * nom de la collegue, pas son identifiant technique.
 *
 * On prend le nom du compte quand il existe. Sinon -- et c'est le cas des
 * commentaires deja ecrits, qui ne portent que l'adresse -- on la reconstitue
 * a partir de la partie qui precede le @ : « nicolas.desjardins » devient
 * « Nicolas Desjardins ». Ce n'est pas parfait, c'est toujours mieux que
 * d'afficher une adresse.
 */
export function nomLisible(valeur) {
  const brut = String(valeur || "").trim();
  if (!brut) return "";
  if (!brut.includes("@")) return brut;
  const identifiant = brut.split("@")[0];
  const mots = identifiant
    .split(/[._-]+/)
    .filter(Boolean)
    .map((m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase());
  return mots.length ? mots.join(" ") : brut;
}

/** Le libelle d'un statut, pour l'historique. */
export function libelleStatut(statut) {
  if (statut === STATUT_TRAITE) return "Dossier traité";
  if (statut === STATUT_A_VERIFIER) return "À vérifier";
  return "Statut initial";
}
